import type { LaneView, MemberEffort } from "../../../api/work";
import { cycleTimeEntries } from "../../../api/work";
import { AvatarChip } from "./AvatarChip";
import { CellTrendSparkline } from "./CellTrendSparkline";
import { CycleTimeStrip } from "./CycleTimeStrip";
import { HandoffStrip } from "./HandoffStrip";
import { StateCell } from "./StateCell";
import {
  TRENDS_SURFACES,
  TRENDS_TYPE,
} from "./pipelineColors";
import { PIPELINE, cellHeight, layoutCells } from "./pipelineGeometry";
import { handoffsBetween, isBottleneck } from "./pipelineHelpers";

interface Props {
  lane: LaneView;
  laneY: number;
  manpower: MemberEffort[];
  reworkThreshold?: number;
  /** `category → colour` for every lane, for members drifted in from another. */
  laneColors?: Record<string, string>;
}

export function LaneRow({ lane, laneY, manpower, reworkThreshold = 3, laneColors = {} }: Props) {
  const nonTerminalStates = lane.states;
  const cellLayout = layoutCells(nonTerminalStates.length);
  const laneCounts = nonTerminalStates.map((s) => s.count);
  const maxCount = Math.max(1, ...laneCounts);

  const nativeMembers = manpower.filter((m) => m.native_lane === lane.category);
  const driftedIn = lane.drifted_members ?? [];

  // The lane's own colour, from the route. The predecessor looked this up
  // in a map keyed by four hardcoded categories; lanes are configuration.
  const laneColor = lane.color;
  const fontBase = "Inter, -apple-system, sans-serif";
  // Pulled out once with fallbacks: `TRENDS_TYPE` is keyed by an open
  // string, so every index is possibly-undefined and indexing it inline
  // six times meant six assertions.
  const labelType = TRENDS_TYPE.laneLabel ?? {
    fontSize: 11,
    fontWeight: 700,
    fill: "#e2e4ea",
  };
  const subType = TRENDS_TYPE.laneSubLabel ?? {
    fontSize: 8,
    fontWeight: 500,
    fill: "#555970",
  };

  const completedCount = lane.throughput?.window_total ?? 0;
  const doneH = Math.max(14, Math.min(PIPELINE.cellMaxHeight, completedCount / 2));
  const doneY = PIPELINE.cellRowBottom - doneH;
  const totalMembers = nativeMembers.length + driftedIn.length;

  return (
    <g transform={`translate(${PIPELINE.laneX}, ${laneY})`}>
      {/* Card background */}
      <rect
        x={0} y={0}
        width={PIPELINE.laneWidth} height={PIPELINE.laneHeight}
        fill={TRENDS_SURFACES.cardFill}
        stroke="#222634" strokeWidth={1}
        rx={4}
      />
      {/* Left color gutter */}
      <rect
        x={0} y={0}
        width={3} height={PIPELINE.laneHeight}
        fill={laneColor}
      />

      {/* Header: lane label + sub-label */}
      <text
        x={PIPELINE.lanePaddingX} y={PIPELINE.laneHeaderY}
        fontFamily={fontBase}
        fontSize={labelType.fontSize}
        fontWeight={labelType.fontWeight}
        fill={labelType.fill}
        letterSpacing="0.04em"
      >
        {lane.label}
      </text>
      <text
        x={PIPELINE.lanePaddingX + 44} y={PIPELINE.laneHeaderY}
        fontFamily={fontBase}
        fontSize={subType.fontSize}
        fontWeight={subType.fontWeight}
        fill={subType.fill}
      >
        {nonTerminalStates.length} state{nonTerminalStates.length === 1 ? "" : "s"}
        {totalMembers > 0
          ? ` · ${totalMembers} member${totalMembers === 1 ? "" : "s"}`
          : ""}
      </text>

      {/* Avatars (top-right of header row) */}
      {nativeMembers.map((m, i) => {
        const dimmed = driftedIn.some((d) => d.name === m.person);
        // `m.wip_tier` in the predecessor; the route nests it under `wip`,
        // which is null when the member has no baseline to compare against.
        // The tiers are `light` / `normal` / `over` — there is no `under`,
        // so the old branch for it could never fire.
        const tier = m.wip?.tier ?? null;
        const ringStyle =
          tier === "over" ? "over" :
          tier === "light" ? "under" :
          "baseline";
        return (
          <AvatarChip
            key={m.person}
            cx={PIPELINE.laneWidth - 14 - i * 18}
            cy={PIPELINE.laneAvatarRowY}
            name={m.person}
            nativeLane={m.native_lane ?? ""}
            laneColor={lane.color}
            ringStyle={ringStyle}
            opacity={dimmed ? 0.35 : 1}
          />
        );
      })}
      {driftedIn.map((d, i) => (
        <AvatarChip
          key={`drift-${d.name}`}
          cx={PIPELINE.laneWidth - 14 - (nativeMembers.length + i) * 18}
          cy={PIPELINE.laneAvatarRowY}
          name={d.name}
          nativeLane={d.from_lane}
          laneColor={laneColors[d.from_lane]}
          ringStyle="drift"
          badgeText={`${d.days_sustained}d`}
        />
      ))}

      {/* The predecessor drew up to three finding chips here, filtered by
          `lane_primary` and a confidence floor. `/api/work/pulse/latest`
          serves findings with both fields, but nothing has produced one
          since the 2026-09-25 cut-over: every finding it has is a migrated
          one, and chips would present them as current. Omitted until a
          producer exists; recorded in TODO.md. */}

      {/* Sparkline above cells — count distribution across workflow */}
      <CellTrendSparkline
        cellLayout={cellLayout}
        states={nonTerminalStates}
        y={PIPELINE.sparklineY}
        height={PIPELINE.sparklineHeight}
        laneColor={laneColor}
      />

      {/* State cells (non-terminal) */}
      {nonTerminalStates.map((state, i) => {
        const slot = cellLayout[i];
        // No slot, no cell. `layoutCells` is called with this same length,
        // so this is unreachable; drawing at x=0 would not be.
        if (!slot) return null;
        const h = cellHeight(state.count, maxCount);
        return (
          <StateCell
            key={state.name}
            x={slot.x}
            width={slot.width}
            heightCell={h}
            state={state}
            reworkThreshold={reworkThreshold}
            isBottleneck={isBottleneck(state.count, laneCounts)}
          />
        );
      })}

      {/* Vertical divider between in-flight cells and Done cell */}
      <line
        x1={PIPELINE.doneCellDividerX} y1={PIPELINE.cellRowTop - 4}
        x2={PIPELINE.doneCellDividerX} y2={PIPELINE.cellRowBottom + 6}
        stroke={TRENDS_SURFACES.divider} strokeWidth={1}
      />

      {/* Done cell */}
      <g>
        <rect
          x={PIPELINE.doneCellX} y={doneY}
          width={PIPELINE.doneCellWidth} height={doneH}
          fill={laneColor} fillOpacity={0.18}
          stroke={laneColor} strokeOpacity={0.5} strokeWidth={1} rx={2}
        />
        <text
          x={PIPELINE.doneCellX + PIPELINE.doneCellWidth / 2}
          y={doneY + doneH / 2 + 3}
          fontFamily={fontBase}
          fontSize={10} fontWeight={700}
          fill={laneColor} textAnchor="middle"
        >
          {completedCount}
        </text>
        <text
          x={PIPELINE.doneCellX + PIPELINE.doneCellWidth / 2}
          y={PIPELINE.stateLabelY}
          fontFamily={fontBase}
          fontSize={6} fontWeight={600}
          fill={laneColor} textAnchor="middle" letterSpacing="0.06em"
        >
          DONE · 30D
        </text>
      </g>

      {/* Cycle-time strip — ends at cellAreaEnd, before the divider */}
      <CycleTimeStrip
        x={PIPELINE.cellAreaX}
        y={PIPELINE.cycleTimeStripY}
        width={PIPELINE.cellAreaEnd - PIPELINE.cellAreaX}
        height={PIPELINE.cycleTimeStripHeight}
        laneColor={laneColor}
        entries={cycleTimeEntries(lane)}
      />

      {/* How long work queues before each move — on the gap it crosses */}
      <HandoffStrip
        cellLayout={cellLayout}
        handoffs={handoffsBetween(
          nonTerminalStates.map((s) => s.name),
          lane.handoff_latencies ?? [],
        )}
        y={PIPELINE.handoffStripY}
        laneColor={laneColor}
      />
    </g>
  );
}
